import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Paper,
  Skeleton,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ShoppingCart as ProductIcon,
  Receipt as OrderIcon,
  LocalShipping as TrackingIcon,
  People as CustomerIcon,
  AttachMoney as PriceIcon
} from '@mui/icons-material';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { formatDate } from '../utils/formatDate';
import { getSyncLogs } from '../services/api';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// Note: fetchSyncLogsSummary function removed since we now use overallData for charts

// Fetch recent sync logs
const fetchRecentSyncLogs = async () => {
  const response = await getSyncLogs({
    limit: 5,
    offset: 0
  });

  return response.data?.data?.logs || [];
};

export default function Dashboard() {

  const [erpToShopifyCustomerStats, setErpToShopifyCustomerStats] = useState({
    total_customers: 0,
    last_synced: null
  });

  const [shopifyToErpCustomerStats, setShopifyToErpCustomerStats] = useState({
    total_customers: 0,
    last_synced: null
  });

  const [productStats, setProductStats] = useState({
    total_products: 0,
    last_synced: null
  });

  const [pricingStats, setPricingStats] = useState({
    total_prices: 0,
    last_synced: null
  });

  const [orderStats, setOrderStats] = useState({
    total_orders: 0,
    last_synced: null
  });

  const [trackingStats, setTrackingStats] = useState({
    total_tracking: 0,
    last_synced: null
  });

  const [chartData, setChartData] = useState({
    bar: {
      labels: ['Products', 'Customer (S→E)', 'Customer (E→S)', 'Pricing', 'Orders', 'Tracking'],
      datasets: [
        {
          label: 'Success',
          data: [0, 0, 0, 0, 0, 0],
          backgroundColor: 'rgba(16, 185, 129, 0.8)'
        },
        {
          label: 'Partial Success',
          data: [0, 0, 0, 0, 0, 0],
          backgroundColor: 'rgba(255, 176, 32, 0.8)'
        },
        {
          label: 'Failed',
          data: [0, 0, 0, 0, 0, 0],
          backgroundColor: 'rgba(209, 67, 67, 0.8)'
        }
      ]
    },
    pie: {
      labels: ['Shopify to ERP', 'ERP to Shopify'],
      datasets: [
        {
          data: [0, 0],
          backgroundColor: [
            'rgba(80, 72, 229, 0.8)',
            'rgba(16, 185, 129, 0.8)'
          ]
        }
      ]
    }
  });

  // Note: summaryData query removed since we now use overallData for chart

  // Fetch recent sync logs
  const { data: recentLogs, isLoading: isLogsLoading } = useQuery({
    queryKey: ['recentSyncLogs'],
    queryFn: fetchRecentSyncLogs,
    staleTime: 0
  });

  // Fetch overall data
  const { isLoading: isOverallLoading } = useQuery({
    queryKey: ['overallData'],
    queryFn: async () => {
      try {
        // Fetch main summary data
        const response = await ApiService.getOverallData();

        if (response.data.data) {
          const {
            erpToShopifySyncedCustomers = {},
            shopifyToErpSyncedCustomers = {},
            products = {},
            orders = {},
            pricing = {},
            tracking = {}
          } = response.data.data;

          setProductStats({
            total_products: products.total || 0,
            last_synced: products.last_synced || null,
            success: products.success || 0,
            failed: products.failed || 0,
            partial_success: products.partial_success || 0
          });

          setErpToShopifyCustomerStats({
            total_customers: erpToShopifySyncedCustomers.total || 0,
            last_synced: erpToShopifySyncedCustomers.last_synced || null,
            success: erpToShopifySyncedCustomers.success || 0,
            failed: erpToShopifySyncedCustomers.failed || 0,
            partial_success: erpToShopifySyncedCustomers.partial_success || 0
          });

          setShopifyToErpCustomerStats({
            total_customers: shopifyToErpSyncedCustomers.total || 0,
            last_synced: shopifyToErpSyncedCustomers.last_synced || null,
            success: shopifyToErpSyncedCustomers.success || 0,
            failed: shopifyToErpSyncedCustomers.failed || 0,
            partial_success: shopifyToErpSyncedCustomers.partial_success || 0
          });

          setOrderStats({
            total_orders: orders.total || 0,
            last_synced: orders.last_synced || null,
            success: orders.success || 0,
            failed: orders.failed || 0,
            partial_success: orders.partial_success || 0
          });

          setPricingStats({
            total_prices: pricing.total || 0,
            last_synced: pricing.last_synced || null,
            success: pricing.success || 0,
            failed: pricing.failed || 0,
            partial_success: pricing.partial_success || 0
          });

          setTrackingStats({
            total_tracking: tracking.total || 0,
            last_synced: tracking.last_synced || null,
            success: tracking.success || 0,
            failed: tracking.failed || 0,
            partial_success: tracking.partial_success || 0
          });
        }

        return response.data;
      } catch (error) {
        console.error('Failed to fetch overall data:', error);
        throw error;
      }
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 0,
    cacheTime: 0
  });

  const navigate = useNavigate();

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const chartRef = useRef(null);

  // Update chart data when stats change
  useEffect(() => {
    // Update chart data
    setChartData({
      bar: {
        labels: ['Products', 'Customer (S→E)', 'Customer (E→S)', 'Pricing', 'Orders', 'Tracking'],
        datasets: [
          {
            label: 'Success',
            data: [
              productStats.success || 0,
              shopifyToErpCustomerStats.success || 0,
              erpToShopifyCustomerStats.success || 0,
              pricingStats.success || 0,
              orderStats.success || 0,
              trackingStats.success || 0
            ],
            backgroundColor: 'rgba(16, 185, 129, 0.8)'
          },
          {
            label: 'Partial Success',
            data: [
              productStats.partial_success || 0,
              shopifyToErpCustomerStats.partial_success || 0,
              erpToShopifyCustomerStats.partial_success || 0,
              pricingStats.partial_success || 0,
              orderStats.partial_success || 0,
              trackingStats.partial_success || 0
            ],
            backgroundColor: 'rgba(255, 176, 32, 0.8)'
          },
          {
            label: 'Failed',
            data: [
              productStats.failed || 0,
              shopifyToErpCustomerStats.failed || 0,
              erpToShopifyCustomerStats.failed || 0,
              pricingStats.failed || 0,
              orderStats.failed || 0,
              trackingStats.failed || 0
            ],
            backgroundColor: 'rgba(209, 67, 67, 0.8)'
          }
        ]
      },
      pie: {
        labels: ['Shopify to ERP', 'ERP to Shopify'],
        datasets: [
          {
            data: [
              (shopifyToErpCustomerStats.success || 0) + (shopifyToErpCustomerStats.partial_success || 0) + (shopifyToErpCustomerStats.failed || 0),
              (erpToShopifyCustomerStats.success || 0) + (erpToShopifyCustomerStats.partial_success || 0) + (erpToShopifyCustomerStats.failed || 0) +
              (productStats.success || 0) + (productStats.partial_success || 0) + (productStats.failed || 0) +
              (pricingStats.success || 0) + (pricingStats.partial_success || 0) + (pricingStats.failed || 0) +
              (trackingStats.success || 0) + (trackingStats.partial_success || 0) + (trackingStats.failed || 0)
            ],
            backgroundColor: [
              'rgba(80, 72, 229, 0.8)',
              'rgba(16, 185, 129, 0.8)'
            ]
          }
        ]
      }
    });
  }, [productStats, erpToShopifyCustomerStats, shopifyToErpCustomerStats, pricingStats, orderStats, trackingStats]);

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
    case 'success':
      return 'success.main';
    case 'partial':
    case 'partial_success':
      return 'warning.main';
    case 'failure':
    case 'failed':
      return 'error.main';
    case 'in_progress':
      return 'info.main';
    default:
      return 'text.primary';
    }
  };

  return (
    <Box>
      <Typography
        variant={isMobile ? 'h5' : 'h4'}
        gutterBottom
        sx={{ mb: { xs: 2, sm: 3 } }}
      >
        Dashboard
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: { xs: 3, md: 4 } }}>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 10 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              {isOverallLoading ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Skeleton
                      variant='circular'
                      width={24}
                      sx={{ mr: 1 }}
                    />
                    <Skeleton variant='text' width={80} />
                  </Box>
                  <Skeleton variant='text' width='60%' sx={{ mb: 0.2 }} />
                  <Skeleton variant='text' width='40%' />
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ProductIcon
                      sx={{
                        color: 'primary.main',
                        mr: 1,
                        fontSize: { xs: 20, sm: 24 }
                      }}
                    />
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
                      Products
                    </Typography>
                  </Box>
                  <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ mb: 1 }}>
                    {productStats.total_products}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Last Sync:{' '}
                    {productStats.last_synced
                      ? formatDate(productStats.last_synced)
                      : 'N/A'}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 10 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              {isOverallLoading ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Skeleton
                      variant='circular'
                      width={24}
                      sx={{ mr: 1 }}
                    />
                    <Skeleton variant='text' width={80} />
                  </Box>
                  <Skeleton variant='text' width='60%' sx={{ mb: 0.2 }} />
                  <Skeleton variant='text' width='40%' />
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CustomerIcon
                      sx={{
                        color: 'primary.main',
                        mr: 1,
                        fontSize: { xs: 20, sm: 24 }
                      }}
                    />
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
                      Customers (ERP to Shopify)
                    </Typography>
                  </Box>
                  <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ mb: 1 }}>
                    {erpToShopifyCustomerStats.total_customers}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Last Sync:{' '}
                    {erpToShopifyCustomerStats.last_synced
                      ? formatDate(erpToShopifyCustomerStats.last_synced)
                      : 'N/A'}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 10 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              {isOverallLoading ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Skeleton
                      variant='circular'
                      width={24}
                      sx={{ mr: 1 }}
                    />
                    <Skeleton variant='text' width={80} />
                  </Box>
                  <Skeleton variant='text' width='60%' sx={{ mb: 0.2 }} />
                  <Skeleton variant='text' width='40%' />
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CustomerIcon
                      sx={{
                        color: 'primary.main',
                        mr: 1,
                        fontSize: { xs: 20, sm: 24 }
                      }}
                    />
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
                      Customers (Shopify to ERP)
                    </Typography>
                  </Box>
                  <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ mb: 1 }}>
                    {shopifyToErpCustomerStats.total_customers}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Last Sync:{' '}
                    {shopifyToErpCustomerStats.last_synced
                      ? formatDate(shopifyToErpCustomerStats.last_synced)
                      : 'N/A'}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 10 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              {isOverallLoading ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Skeleton
                      variant='circular'
                      width={24}
                      sx={{ mr: 1 }}
                    />
                    <Skeleton variant='text' width={80} />
                  </Box>
                  <Skeleton variant='text' width='60%' sx={{ mb: 0.2 }} />
                  <Skeleton variant='text' width='40%' />
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PriceIcon
                      sx={{
                        color: 'primary.main',
                        mr: 1,
                        fontSize: { xs: 20, sm: 24 }
                      }}
                    />
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
                      Price
                    </Typography>
                  </Box>
                  <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ mb: 1 }}>
                    {pricingStats.total_prices}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Last Sync:{' '}
                    {pricingStats.last_synced
                      ? formatDate(pricingStats.last_synced)
                      : 'N/A'}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 10 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              {isOverallLoading ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Skeleton
                      variant='circular'
                      width={24}
                      sx={{ mr: 1 }}
                    />
                    <Skeleton variant='text' width={80} />
                  </Box>
                  <Skeleton variant='text' width='60%' sx={{ mb: 0.2 }} />
                  <Skeleton variant='text' width='40%' />
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <OrderIcon
                      sx={{
                        color: 'primary.main',
                        mr: 1,
                        fontSize: { xs: 20, sm: 24 }
                      }}
                    />
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
                      Orders
                    </Typography>
                  </Box>
                  <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ mb: 1 }}>
                    {orderStats.total_orders}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Last Sync:{' '}
                    {orderStats.last_synced
                      ? formatDate(orderStats.last_synced)
                      : 'N/A'}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 10 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              {isOverallLoading ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Skeleton
                      variant='circular'
                      width={24}
                      sx={{ mr: 1 }}
                    />
                    <Skeleton variant='text' width={80} />
                  </Box>
                  <Skeleton variant='text' width='60%' sx={{ mb: 0.2 }} />
                  <Skeleton variant='text' width='40%' />
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrackingIcon
                      sx={{
                        color: 'primary.main',
                        mr: 1,
                        fontSize: { xs: 20, sm: 24 }
                      }}
                    />
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
                      Tracking
                    </Typography>
                  </Box>
                  <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ mb: 1 }}>
                    {trackingStats.total_tracking}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Last Sync:{' '}
                    {trackingStats.last_synced
                      ? formatDate(trackingStats.last_synced)
                      : 'N/A'}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={2} sx={{ mb: { xs: 3, md: 4 } }}>
        <Grid item xs={12} lg={12}>
          <Card sx={{ boxShadow: 10 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
                Sync Results by Type
              </Typography>
              <Box
                sx={{
                  height: { xs: 250, sm: 300 },
                  position: 'relative',
                  width: '100%'
                }}
              >
                {isOverallLoading ? (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Skeleton variant='text' width='20%' height={25} />
                    </Box>
                    <Skeleton
                      variant='rectangular'
                      sx={{
                        borderRadius: 1
                      }}
                      width='100%'
                      height='100%'
                    />
                  </Box>
                ) : (
                  <Bar
                    ref={chartRef}
                    data={chartData.bar}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: isMobile ? 'bottom' : 'top',
                          labels: {
                            boxWidth: isMobile ? 12 : 20,
                            font: {
                              size: isMobile ? 10 : 12
                            }
                          }
                        }
                      },
                      scales: {
                        x: {
                          ticks: {
                            font: {
                              size: isMobile ? 10 : 12
                            }
                          }
                        },
                        y: {
                          beginAtZero: true,
                          ticks: {
                            font: {
                              size: isMobile ? 10 : 12
                            }
                          }
                        }
                      }
                    }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Sync Logs */}
      <Card sx={{ boxShadow: 10 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 4,
              gap: { xs: 1, sm: 0 }
            }}
          >
            <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
              Recent Sync Logs
            </Typography>
            <Button
              variant='contained'
              color='secondary'
              size='medium'
              onClick={() => navigate('/sync-logs')}
              disabled={isLogsLoading}
            >
              View All
            </Button>
          </Box>

          {isLogsLoading ? (
            <>
              {[...Array(5)].map((_, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Skeleton variant='circular' width={8} height={8} />
                      <Skeleton variant='text' width={150} height={24} />
                    </Box>
                    <Skeleton variant='text' width={120} height={20} />
                  </Box>
                  <Skeleton
                    variant='text'
                    width='80%'
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  {index < 4 && <Divider sx={{ my: 2 }} />}
                </Box>
              ))}
            </>
          ) : recentLogs && recentLogs.length > 0 ? (
            <Box
              sx={{
                overflowX: isMobile ? 'auto' : 'hidden',
                '&::-webkit-scrollbar': {
                  height: '8px'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '4px'
                }
              }}
            >
              {recentLogs.map((log, index) => (
                <Box key={log.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                      flexDirection: {
                        xs: isMobile ? 'column' : 'row',
                        sm: 'row'
                      },
                      gap: { xs: 0.5, sm: 0 }
                    }}
                  >
                    <Typography
                      variant={isMobile ? 'body2' : 'subtitle1'}
                      component='span'
                      sx={{
                        color: getStatusColor(log.status),
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: getStatusColor(log.status)
                        }}
                      />
                      {log.sync_type?.toUpperCase()} (
                      {log.sync_sub_type?.split('_').join(' ').toUpperCase()})
                      SYNC - {log.status?.split('_').join(' ').toUpperCase()}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {formatDate(log.created_at)}
                    </Typography>
                  </Box>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{
                      mb: 1,
                      fontSize: { xs: 12, sm: 14 },
                      lineHeight: 1.5
                    }}
                  >
                    {log.message}
                  </Typography>
                  {index < recentLogs.length - 1 && <Divider sx={{ my: 2 }} />}
                </Box>
              ))}
            </Box>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant='body1'>No recent sync logs found</Typography>
            </Paper>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
